import { useRef } from 'react';
import { MatchCard } from './match-card';
import { BracketConnectors, type ConnectorLink } from './bracket-connectors';
import {
  computeBracketLayout,
  BRACKET_CARD_H,
  BRACKET_COL_W,
  BRACKET_COL_GAP,
} from './bracket-layout';
import { roundProgressLabel } from '@/lib/match-format';
import type { Match } from '@/data/types';

interface Props {
  matches: Match[];
  rounds: number;
  onSelectMatch?: (match: Match) => void;
  placeByParticipant?: Map<string, string>;
}

/**
 * Single Elimination сетка. Раскладка — общий движок computeBracketLayout:
 * матч раунда N центрирован между парой питающих его матчей раунда N-1.
 */
export function SingleElimBracket({ matches, rounds, onSelectMatch, placeByParticipant }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const ids = new Set(matches.map((m) => m.id));
  const links: ConnectorLink[] = matches
    .filter((m) => m.nextMatchId && ids.has(m.nextMatchId))
    .map((m) => ({ fromId: m.id, toId: m.nextMatchId as string }));

  const layout = computeBracketLayout(matches);
  const totalRounds = rounds;

  return (
    <div ref={containerRef} className="bracket-scroll" style={{ position: 'relative' }}>
      <BracketConnectors containerRef={containerRef} links={links} />
      <div className="relative" style={{ width: layout.width, height: layout.height }}>
        {layout.rounds.map((round, roundIdx) => {
          const inRound = matches.filter((m) => m.round === round);
          const left = roundIdx * (BRACKET_COL_W + BRACKET_COL_GAP);
          return (
            <div
              key={round}
              className="absolute bracket-col-head"
              style={{ left, top: 0, width: BRACKET_COL_W }}
            >
              {roundLabel(round, totalRounds)} <span className="count">{roundProgressLabel(inRound)}</span>
            </div>
          );
        })}
        {layout.positioned.map(({ match, left, top }) => (
          <div
            key={match.id}
            data-match-id={match.id}
            className="absolute"
            style={{ left, top, width: BRACKET_COL_W, height: BRACKET_CARD_H }}
          >
            <MatchCard
              match={match}
              onClick={() => onSelectMatch?.(match)}
              placeByParticipant={placeByParticipant}
              fill
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function roundLabel(round: number, total: number): string {
  if (round === total) return 'Финал';
  if (round === total - 1) return 'Полуфинал';
  if (round === total - 2) return 'Четвертьфинал';
  return `Раунд ${round}`;
}
